import { spawnSync } from 'child_process';
import { createHash } from 'crypto';
import { existsSync, mkdirSync, readFileSync, writeFileSync } from 'fs';
import { tmpdir } from 'os';
import { dirname, join } from 'path';
import { generateStrongPassword } from './dev-configure.js';

// ============================================================================
// DataProtection key-ring certificate — generation
//
// The platform persists its ASP.NET DataProtection key ring in the database as
// soon as a connection string is set, and refuses to start outside
// Development/Local unless DataProtection:Certificate encrypts it. This module
// produces that certificate WITHOUT any npm dependency: it runs the .NET 10
// file-based app shipped at templates/tools/dataprotection-cert.cs, which builds
// the certificate with the BCL, re-loads the PFX the way the platform does and
// proves the private key works before handing it back.
//
// The password is generated here and passed through the environment, never in
// argv. The PFX comes back as base64 on the child's stdout (a pipe this module
// reads and never echoes).
// ============================================================================

export const CERT_TOOL_FILE = 'dataprotection-cert.cs';
export const CERT_TOOL_PASSWORD_ENV = 'SMARTSTACK_DPCERT_PASSWORD';
export const CERT_TOOL_RESULT_MARKER = 'SMARTSTACK_DPCERT_RESULT ';

/** Length of the generated PFX password (generateStrongPassword alphabet). */
export const PFX_PASSWORD_LENGTH = 40;

/** First run compiles the file-based app (restore + build): give it room. */
const DEFAULT_TIMEOUT_MS = 180_000;

export interface DataProtectionCertificate {
  pfxBase64: string;
  password: string;
  thumbprint: string;
  subject: string;
  notBefore: string;
  notAfter: string;
}

export interface CertSpawnResult {
  status: number | null;
  stdout: string;
  stderr: string;
  error?: Error;
}

/** Injectable process runner (tests replace it; production uses spawnSync). */
export type CertSpawn = (
  command: string,
  args: string[],
  options: { cwd: string; env: NodeJS.ProcessEnv; timeoutMs: number },
) => CertSpawnResult;

export interface GenerateCertificateOptions {
  /** Project name, written into the certificate subject. */
  projectName: string;
  /** Target environment (docker, production, …), written into the subject. */
  environment: string;
  /** Validity in years (default 5). */
  years?: number;
  /** Defaults to the tool shipped in the package (templates/tools/). */
  toolPath?: string;
  /** Where the tool is staged before running (default: OS temp dir). */
  stagingRoot?: string;
  spawn?: CertSpawn;
  /** Override the generated password (tests). */
  password?: string;
  timeoutMs?: number;
}

const defaultSpawn: CertSpawn = (command, args, { cwd, env, timeoutMs }) => {
  const result = spawnSync(command, args, {
    cwd,
    env,
    encoding: 'utf-8',
    timeout: timeoutMs,
    windowsHide: true,
    maxBuffer: 16 * 1024 * 1024,
  });
  return {
    status: result.status,
    stdout: result.stdout ?? '',
    stderr: result.stderr ?? '',
    error: result.error,
  };
};

/**
 * Locate templates/tools/dataprotection-cert.cs by walking up from this module:
 * `dist/` once bundled, `src/lib/` under vitest — both reach the package root.
 */
export function resolveCertToolPath(startDir: string = __dirname): string {
  let dir = startDir;
  for (let i = 0; i < 6; i++) {
    const candidate = join(dir, 'templates', 'tools', CERT_TOOL_FILE);
    if (existsSync(candidate)) return candidate;
    const parent = dirname(dir);
    if (parent === dir) break;
    dir = parent;
  }
  throw new Error(`${CERT_TOOL_FILE} not found in the CLI package (templates/tools/) — reinstall @atlashub/smartstack-cli.`);
}

/**
 * Copy the tool into a content-addressed folder of the OS temp dir and run it
 * from there. Two reasons: a file-based app imports the Directory.Build.props /
 * Directory.Packages.props of every ancestor folder — an `ss` installed inside a
 * client repo would build the tool with that repo's MSBuild settings — and the
 * empty shields below stop that search; the stable path keeps the SDK's build
 * cache warm across runs.
 */
export function stageCertTool(toolPath: string, stagingRoot: string = join(tmpdir(), 'smartstack-cli', 'dataprotection-cert')): string {
  const source = readFileSync(toolPath, 'utf-8');
  const hash = createHash('sha256').update(source).digest('hex').slice(0, 12);
  const dir = join(stagingRoot, hash);
  const staged = join(dir, CERT_TOOL_FILE);
  if (!existsSync(staged) || readFileSync(staged, 'utf-8') !== source) {
    mkdirSync(dir, { recursive: true });
    const shield = '<Project>\n  <!-- Stops MSBuild from importing an ancestor folder\'s file (SmartStack CLI staging). -->\n</Project>\n';
    writeFileSync(join(dir, 'Directory.Build.props'), shield, 'utf-8');
    writeFileSync(join(dir, 'Directory.Build.targets'), shield, 'utf-8');
    writeFileSync(
      join(dir, 'Directory.Packages.props'),
      '<Project>\n  <PropertyGroup>\n    <ManagePackageVersionsCentrally>false</ManagePackageVersionsCentrally>\n  </PropertyGroup>\n</Project>\n',
      'utf-8',
    );
    writeFileSync(staged, source, 'utf-8');
  }
  return staged;
}

/**
 * Extract the result line from the tool's stdout. The SDK may print build
 * output (warnings) on stdout before it, hence the marker.
 */
export function parseCertToolOutput(stdout: string): Omit<DataProtectionCertificate, 'password'> {
  const line = stdout
    .split(/\r?\n/)
    .find((l) => l.startsWith(CERT_TOOL_RESULT_MARKER));
  if (!line) {
    throw new Error('the certificate tool produced no result line');
  }
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(line.slice(CERT_TOOL_RESULT_MARKER.length)) as Record<string, unknown>;
  } catch {
    throw new Error('the certificate tool produced an unreadable result line');
  }
  const fields = ['pfxBase64', 'thumbprint', 'subject', 'notBefore', 'notAfter'] as const;
  for (const field of fields) {
    if (typeof parsed[field] !== 'string' || (parsed[field] as string).length === 0) {
      throw new Error(`the certificate tool result lacks "${field}"`);
    }
  }
  return {
    pfxBase64: parsed.pfxBase64 as string,
    thumbprint: parsed.thumbprint as string,
    subject: parsed.subject as string,
    notBefore: parsed.notBefore as string,
    notAfter: parsed.notAfter as string,
  };
}

/** Keep the tail of a failed run's stderr — build errors can be long. */
function tail(text: string, lines = 12): string {
  return text.trim().split(/\r?\n/).slice(-lines).join('\n');
}

const MANUAL_FALLBACK =
  'Without the .NET 10 SDK, create the PFX by hand: ' +
  'openssl req -x509 -newkey rsa:3072 -sha256 -days 1826 -nodes -subj "/CN=SmartStack DataProtection" -keyout dp.key -out dp.crt ' +
  '&& openssl pkcs12 -export -inkey dp.key -in dp.crt -out dataprotection.pfx — then base64 it into DATAPROTECTION_PFX_BASE64 and put its password in DATAPROTECTION_PFX_PASSWORD.';

/**
 * Generate a DataProtection certificate. Synchronous: it is one short child
 * process (a few seconds on first run, < 1 s once the SDK has cached the build).
 */
export function generateDataProtectionCertificate(opts: GenerateCertificateOptions): DataProtectionCertificate {
  const spawn = opts.spawn ?? defaultSpawn;
  const password = opts.password ?? generateStrongPassword(PFX_PASSWORD_LENGTH);
  const staged = stageCertTool(opts.toolPath ?? resolveCertToolPath(), opts.stagingRoot);

  const args = ['run', staged, '--', '--project', opts.projectName, '--env', opts.environment];
  if (opts.years !== undefined) args.push('--years', String(opts.years));

  const result = spawn('dotnet', args, {
    cwd: dirname(staged),
    env: {
      ...process.env,
      [CERT_TOOL_PASSWORD_ENV]: password,
      DOTNET_NOLOGO: '1',
      DOTNET_CLI_TELEMETRY_OPTOUT: '1',
    },
    timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
  });

  if (result.error) {
    const code = (result.error as NodeJS.ErrnoException).code;
    if (code === 'ENOENT') {
      throw new Error(`dotnet was not found on PATH — the certificate is generated by a .NET 10 file-based app. ${MANUAL_FALLBACK}`);
    }
    throw new Error(`could not run the certificate tool: ${result.error.message}`);
  }
  if (result.status !== 0) {
    const detail = tail(result.stderr) || tail(result.stdout);
    const sdkHint = /file-based|\.cs.*(not found|does not exist)|MSB1003|MSB1009|Couldn't find a project/i.test(detail)
      ? ` This needs the .NET 10 SDK (file-based apps). ${MANUAL_FALLBACK}`
      : '';
    throw new Error(`the certificate tool failed (exit ${result.status ?? 'timeout'}):\n${detail}${sdkHint}`);
  }

  return { ...parseCertToolOutput(result.stdout), password };
}
